/**
 * KAN-755: на что действует скидка: переключатель "Весь каталог" или выбор
 * товаров (поиск, чипы), категорий (дерево, вместе с подкатегориями) и групп.
 */
import type { ReactNode } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toggleId, type ScopeValues } from '@/lib/discountForm';
import { CategoryCheckTree } from './CategoryCheckTree';
import { GroupMultiPicker } from './GroupMultiPicker';
import { ProductMultiPicker } from './ProductMultiPicker';

interface ScopePickerProps {
  value: ScopeValues;
  onChange: (patch: Partial<ScopeValues>) => void;
  error?: string;
  disabled?: boolean;
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <h4 className="text-sm font-medium text-gray-800">
        {title}
        {count > 0 && <span className="ml-1 text-gray-500">({count})</span>}
      </h4>
      {children}
    </div>
  );
}

export function ScopePicker({ value, onChange, error, disabled = false }: ScopePickerProps) {
  return (
    <fieldset className="space-y-3 rounded-lg border p-3" data-testid="discount-scope">
      <legend className="px-1 text-sm font-medium text-gray-900">На что действует</legend>
      <div className="flex items-center gap-2">
        <Switch
          id="discount-scope-all"
          checked={value.appliesToAll}
          disabled={disabled}
          onCheckedChange={(checked) => onChange({ appliesToAll: checked })}
          data-testid="discount-scope-all"
        />
        <Label htmlFor="discount-scope-all">Весь каталог</Label>
      </div>
      {!value.appliesToAll && (
        <div className="space-y-4">
          <Section title="Товары" count={value.products.length}>
            <ProductMultiPicker
              value={value.products}
              onChange={(products) => onChange({ products })}
              disabled={disabled}
            />
          </Section>
          <Section title="Категории" count={value.categoryIds.length}>
            <CategoryCheckTree
              value={value.categoryIds}
              onToggle={(id) => onChange({ categoryIds: toggleId(value.categoryIds, id) })}
              disabled={disabled}
            />
          </Section>
          <Section title="Группы" count={value.groupIds.length}>
            <GroupMultiPicker
              value={value.groupIds}
              onToggle={(id) => onChange({ groupIds: toggleId(value.groupIds, id) })}
              disabled={disabled}
            />
          </Section>
        </div>
      )}
      {error && (
        <p className="text-xs text-red-700" data-testid="discount-scope-error">
          {error}
        </p>
      )}
    </fieldset>
  );
}
